/**
 * HTMLStructureTree
 *
 * Renders an SVGStructure as semantic HTML inside an SVG <foreignObject>, for
 * screen-reader navigation of mechanism SVGs. Uses <details>/<summary> for
 * collapsible containers (molecules, features, atoms) and <ul>/<li> for leaves
 * (bonds, arrows).
 *
 * MC-886: the tree is alt-text-only. It has no click/focus/keyboard
 * interaction wiring, no selection prop surface, and no lone-pair or
 * bond-endpoint child nodes — bonds always render as flat <li>s. Atoms in
 * `hiddenAtomIds` (and bonds touching them) are filtered out.
 *
 * Two render modes via the `asSVG` prop:
 * - `asSVG={true}` (default): wraps the tree in <foreignObject> inside SVG.
 *   The tree ships with exported mechanism SVGs for screen-reader access.
 *   `debugVisible` toggles between visually-hidden (WCAG) and a styled debug
 *   overlay positioned over the SVG.
 * - `asSVG={false}`: renders the inner tree <div> directly as XHTML, with no
 *   <svg> / <foreignObject> wrapper and no inline styles. Use this when the
 *   tree is the page content itself (e.g. a sidebar or preview panel); the
 *   caller owns layout and styling via the parent element.
 *
 * The `labelMode` prop controls how each node's name + description are
 * presented:
 * - `'combined'` (default): summary shows "name: description" inline. Best for
 *   screen readers walking the alt-text tree.
 * - `'progressive'`: summary shows the name only; the description appears as a
 *   styled <p> inside the <details>, visible after expansion. Better for
 *   sighted users browsing the visible tree.
 *
 * Nicole's source picker (TreeSelect.tsx in @alchemiesolns/nicole-controls)
 * consumes the same underlying `SVGStructure` data via the navigation helpers
 * (`getNodeById`, `getDescendantIds`, type guards) — none of which changed.
 *
 * @package @alchemiesolns/svg-interactive
 */

import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import type {
  SVGStructure,
  MoleculeNode,
  FeatureNode,
  AtomLeaf,
  BondLeaf,
  ArrowLeaf,
} from '../structure/types';
import type { TreeLabelMap, TreeLabel } from '../structure/buildAltTextMap';

/**
 * How node labels are presented.
 * - `'combined'` (default): summary shows "name: description" inline.
 * - `'progressive'`: summary shows the name only; the description appears as a
 *   styled <p> inside the <details>, visible after expansion.
 */
export type HTMLStructureTreeLabelMode = 'combined' | 'progressive';

/**
 * Imperative handle for HTMLStructureTree.
 * Allows parent components to control tree expansion programmatically.
 */
export interface HTMLStructureTreeHandle {
  /** Expand all collapsible nodes in the tree */
  expandAll: () => void;
  /** Collapse all collapsible nodes in the tree */
  collapseAll: () => void;
  /** Reset tree to initial state: collapse all nodes */
  reset: () => void;
}

export interface HTMLStructureTreeProps {
  /** The molecular structure to render */
  structure: SVGStructure;
  /** Map of element IDs to tree labels (name + optional description). */
  treeLabelMap?: TreeLabelMap;
  /**
   * X position in SVG coordinate space.
   * Defaults to a far off-screen value so the screen-reader focus
   * indicator (e.g. VoiceOver cursor) doesn't render on top of the SVG.
   * When `debugVisible` is true, x/y default to 0 so the panel is visible.
   */
  x?: number;
  /** Y position in SVG coordinate space. See {@link x}. */
  y?: number;
  /** Make tree visible for debugging (default: false) */
  debugVisible?: boolean;
  /**
   * Atom IDs to hide from the tree (build via `collectHiddenAtomIds(workspace)`).
   * Bonds touching any of these atoms are also hidden.
   */
  hiddenAtomIds?: Set<string>;
  /**
   * How node labels render. Default `'combined'` preserves the inline
   * "name: description" summary. `'progressive'` shows the name only in the
   * summary and the description as a <p> inside <details>.
   */
  labelMode?: HTMLStructureTreeLabelMode;
  /**
   * Render context. Defaults to `true` — wraps the tree in <foreignObject> so
   * it can be embedded in an SVG. Set to `false` to render the inner tree
   * <div> directly as XHTML with no <svg>/<foreignObject>/inline-style
   * wrapper; the caller is responsible for layout and visible styling. When
   * `false`, `x`, `y`, and `debugVisible` are ignored (SVG-only concepts).
   */
  asSVG?: boolean;
}

/**
 * Shared context for tree items.
 */
interface TreeItemContext {
  treeLabelMap?: TreeLabelMap;
  hiddenAtomIds?: Set<string>;
  labelMode: HTMLStructureTreeLabelMode;
}

/** True when the bond should be hidden because either endpoint is hidden. */
function isBondHidden(bond: BondLeaf, hiddenAtomIds: Set<string> | undefined): boolean {
  if (!hiddenAtomIds || hiddenAtomIds.size === 0) return false;
  return hiddenAtomIds.has(bond.atomIds[0]) || hiddenAtomIds.has(bond.atomIds[1]);
}

/**
 * Get the summary label for a node.
 * - `'combined'`: returns "name: description" when description exists, else "name".
 * - `'progressive'`: returns just the name (the description renders separately).
 */
function getDisplayLabel(
  entry: TreeLabel | undefined,
  fallbackLabel: string,
  labelMode: HTMLStructureTreeLabelMode
): string {
  if (!entry) return fallbackLabel;
  if (labelMode === 'progressive') return entry.name;
  return entry.description
    ? `${entry.name}: ${entry.description}`
    : entry.name;
}

/**
 * Get the description to render in a separate <p> for progressive mode.
 * Returns null for any other mode, or when the entry has no description.
 */
function getProgressiveDescription(
  entry: TreeLabel | undefined,
  labelMode: HTMLStructureTreeLabelMode
): string | null {
  if (labelMode !== 'progressive') return null;
  return entry?.description ?? null;
}

/**
 * Visually hidden but accessible to screen readers (WCAG pattern)
 */
const VISUALLY_HIDDEN_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/**
 * Debug mode - visible for development
 */
const DEBUG_VISIBLE_STYLE: React.CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '10px',
  maxWidth: '400px',
  maxHeight: '500px',
  overflow: 'auto',
  background: 'rgba(255, 255, 255, 0.98)',
  border: '1px solid #ccc',
  borderRadius: '4px',
  padding: '12px',
  fontSize: '13px',
  // MC-946: lead with the brand body font (Montserrat, self-hosted via
  // @alchemiesolns/shared-styles/fonts) so the accessibility panel matches the app and
  // renders consistently across machines, rather than the OS-dependent system-ui.
  fontFamily: 'Montserrat, system-ui, -apple-system, sans-serif',
  lineHeight: '1.4',
  color: '#333',
};

const LIST_STYLE: React.CSSProperties = {
  listStyle: 'none',
  paddingLeft: '16px',
  margin: '4px 0',
};

const SUMMARY_STYLE: React.CSSProperties = {
  cursor: 'default',
  padding: '2px 0',
};

/**
 * Styling for the progressive-mode description <p> that appears inside
 * <details> elements. Keep aligned with `ExperimentalHTMLTree` for visual
 * parity if anyone is comparing branches.
 */
const DESCRIPTION_STYLE: React.CSSProperties = {
  margin: '4px 0 8px 0',
  padding: '4px 8px',
  backgroundColor: 'rgba(59, 130, 246, 0.1)',
  borderLeft: '2px solid rgba(59, 130, 246, 0.5)',
  fontSize: '12px',
  fontStyle: 'italic',
  color: '#555',
};

/**
 * Bond node — always renders as a flat <li>. Bond endpoints are no longer
 * rendered (MC-886).
 */
function HTMLBondItem({
  bond,
  context,
}: {
  bond: BondLeaf;
  context: TreeItemContext;
}) {
  const displayLabel = getDisplayLabel(
    context.treeLabelMap?.get(bond.bondId),
    bond.label,
    context.labelMode
  );
  return <li>{displayLabel}</li>;
}

/**
 * Atom node — renders as <details> when it has visible children, otherwise as
 * a flat <li>. Hidden atoms are skipped entirely; lone pairs are no longer
 * rendered (MC-886).
 */
function HTMLAtomItem({
  atom,
  context,
}: {
  atom: AtomLeaf;
  context: TreeItemContext;
}) {
  if (context.hiddenAtomIds?.has(atom.atomId)) {
    return null;
  }

  const visibleHydrogenNodes = context.hiddenAtomIds
    ? atom.hydrogenNodes.filter((h) => !context.hiddenAtomIds!.has(h.atomId))
    : atom.hydrogenNodes;
  const visibleBondNodes = context.hiddenAtomIds
    ? atom.bondNodes.filter((b) => !isBondHidden(b, context.hiddenAtomIds))
    : atom.bondNodes;

  const labelEntry = context.treeLabelMap?.get(atom.atomId);
  const displayLabel = getDisplayLabel(labelEntry, atom.label, context.labelMode);
  const progressiveDesc = getProgressiveDescription(labelEntry, context.labelMode);
  const hasChildren = visibleHydrogenNodes.length > 0 || visibleBondNodes.length > 0;

  if (!hasChildren) {
    // In progressive mode, an atom with a description but no structural
    // children still needs to be expandable so the description is reachable.
    if (progressiveDesc) {
      return (
        <li>
          <details>
            <summary style={SUMMARY_STYLE}>{displayLabel}</summary>
            <p style={DESCRIPTION_STYLE}>{progressiveDesc}</p>
          </details>
        </li>
      );
    }
    return <li>{displayLabel}</li>;
  }

  return (
    <li>
      <details>
        <summary style={SUMMARY_STYLE}>{displayLabel}</summary>
        {progressiveDesc && <p style={DESCRIPTION_STYLE}>{progressiveDesc}</p>}
        <ul style={LIST_STYLE}>
          {visibleHydrogenNodes.map((h) => (
            <HTMLAtomItem key={h.id} atom={h} context={context} />
          ))}
          {visibleBondNodes.map((bond) => (
            <HTMLBondItem key={bond.id} bond={bond} context={context} />
          ))}
        </ul>
      </details>
    </li>
  );
}

/**
 * Feature node — renders as collapsible <details> with ordered children.
 */
function HTMLFeatureItem({
  feature,
  context,
}: {
  feature: FeatureNode;
  context: TreeItemContext;
}) {
  const labelEntry = context.treeLabelMap?.get(feature.id);
  const displayLabel = getDisplayLabel(labelEntry, feature.label, context.labelMode);
  const progressiveDesc = getProgressiveDescription(labelEntry, context.labelMode);

  const isAtomHidden = (atomId: string) =>
    context.hiddenAtomIds?.has(atomId) === true;

  // Helper to render a child by ID (respects orderedChildIds for correct interleaving).
  const renderChild = (childId: string) => {
    const atom = feature.atoms.find((a) => a.id === childId);
    if (atom) {
      if (isAtomHidden(atom.atomId)) return null;
      return <HTMLAtomItem key={atom.id} atom={atom} context={context} />;
    }

    const childFeature = feature.children.find((f) => f.id === childId);
    if (childFeature) {
      return (
        <HTMLFeatureItem key={childFeature.id} feature={childFeature} context={context} />
      );
    }

    return null;
  };

  return (
    <li>
      <details>
        <summary style={SUMMARY_STYLE}>{displayLabel}</summary>
        {progressiveDesc && <p style={DESCRIPTION_STYLE}>{progressiveDesc}</p>}
        <ul style={LIST_STYLE}>
          {feature.orderedChildIds && feature.orderedChildIds.length > 0 ? (
            feature.orderedChildIds.map(renderChild)
          ) : (
            <>
              {feature.atoms
                .filter((atom) => !isAtomHidden(atom.atomId))
                .map((atom) => (
                  <HTMLAtomItem key={atom.id} atom={atom} context={context} />
                ))}
              {feature.children.map((child) => (
                <HTMLFeatureItem key={child.id} feature={child} context={context} />
              ))}
            </>
          )}
        </ul>
      </details>
    </li>
  );
}

/**
 * Arrow item (PIPH-90): read-only <li> exposing curved-arrow descriptions to
 * screen readers.
 */
function HTMLArrowItem({ arrow }: { arrow: ArrowLeaf }) {
  const displayLabel = arrow.description || arrow.label;
  return <li aria-label={displayLabel}>{displayLabel}</li>;
}

/**
 * Arrows section (PIPH-90): collapsible list of curved arrows at the tree root.
 */
function HTMLArrowsSection({ arrows }: { arrows: ArrowLeaf[] }) {
  if (arrows.length === 0) return null;

  const sectionLabel = `Curved arrows (${arrows.length})`;

  return (
    <details>
      <summary style={SUMMARY_STYLE}>{sectionLabel}</summary>
      <ul style={LIST_STYLE}>
        {arrows.map((arrow) => (
          <HTMLArrowItem key={arrow.id} arrow={arrow} />
        ))}
      </ul>
    </details>
  );
}

/**
 * Molecule node — top-level container for a connected component.
 */
function HTMLMoleculeItem({
  molecule,
  context,
}: {
  molecule: MoleculeNode;
  context: TreeItemContext;
}) {
  const labelEntry = context.treeLabelMap?.get(molecule.id);
  const displayLabel = getDisplayLabel(labelEntry, molecule.label, context.labelMode);
  const progressiveDesc = getProgressiveDescription(labelEntry, context.labelMode);

  const visibleUnclaimedAtoms = context.hiddenAtomIds
    ? molecule.unclaimedAtoms.filter((a) => !context.hiddenAtomIds!.has(a.atomId))
    : molecule.unclaimedAtoms;

  return (
    <details>
      <summary style={SUMMARY_STYLE}>{displayLabel}</summary>
      {progressiveDesc && <p style={DESCRIPTION_STYLE}>{progressiveDesc}</p>}
      <ul style={LIST_STYLE}>
        {molecule.features.map((f) => (
          <HTMLFeatureItem key={f.id} feature={f} context={context} />
        ))}
        {/* Unclaimed atoms render as direct siblings of the features (no wrapper
            section), so feature-less molecules like HBr still expose their atoms. */}
        {visibleUnclaimedAtoms.map((a) => (
          <HTMLAtomItem key={a.id} atom={a} context={context} />
        ))}
      </ul>
    </details>
  );
}

/**
 * HTMLStructureTree — alt-text-only screen-reader navigation surface for
 * molecular structure SVGs. See file header for full description.
 */
export const HTMLStructureTree = forwardRef<HTMLStructureTreeHandle, HTMLStructureTreeProps>(
  function HTMLStructureTree(
    {
      structure,
      treeLabelMap,
      x,
      y,
      debugVisible = false,
      hiddenAtomIds,
      labelMode = 'combined',
      asSVG = true,
    },
    ref
  ) {
    const rootRef = useRef<HTMLDivElement>(null);
    // Park the foreignObject far off-screen unless debugging. The inner
    // div's `clip: rect(0,0,0,0)` hides the *paint*, but VoiceOver/NVDA
    // still draw their focus cursor at the element's reported AX frame,
    // which tracks the foreignObject's SVG position. Moving the frame
    // off-screen takes the focus indicator with it; screen readers walk
    // the accessibility tree, so navigation is unaffected.
    const resolvedX = x ?? (debugVisible ? 0 : -100000);
    const resolvedY = y ?? (debugVisible ? 0 : -100000);

    const context: TreeItemContext = {
      treeLabelMap,
      hiddenAtomIds,
      labelMode,
    };

    const collapseAllDetails = () => {
      if (!rootRef.current) return;
      const details = rootRef.current.querySelectorAll('details');
      details.forEach((detail) => {
        detail.open = false;
      });
    };

    useImperativeHandle(ref, () => ({
      expandAll: () => {
        if (!rootRef.current) return;
        const details = rootRef.current.querySelectorAll('details');
        details.forEach((detail) => {
          detail.open = true;
        });
      },
      collapseAll: collapseAllDetails,
      reset: collapseAllDetails,
    }), []);

    // Shared tree content. Identical markup in both render modes.
    const treeContent = (
      <>
        {structure.tree.description && (
          <p style={{ margin: '0 0 8px 0', fontStyle: 'italic' }}>
            {structure.tree.description}
          </p>
        )}
        {structure.tree.molecules.map((mol) => (
          <HTMLMoleculeItem key={mol.id} molecule={mol} context={context} />
        ))}
        {/* PIPH-90: Arrows section for screen-reader navigation of electron movement */}
        {structure.tree.arrows && structure.tree.arrows.length > 0 && (
          <HTMLArrowsSection arrows={structure.tree.arrows} />
        )}
      </>
    );

    // Standalone (non-SVG) render: plain in-flow <div>, caller owns layout/styling.
    if (!asSVG) {
      return (
        <div
          ref={rootRef}
          role="navigation"
          aria-label={structure.tree.label}
        >
          {treeContent}
        </div>
      );
    }

    // SVG render: embedded in <foreignObject>, with visually-hidden or debug-overlay styling.
    const rootStyle = debugVisible ? DEBUG_VISIBLE_STYLE : VISUALLY_HIDDEN_STYLE;
    return (
      <foreignObject
        x={resolvedX}
        y={resolvedY}
        width="1"
        height="1"
        style={{ overflow: 'hidden', pointerEvents: 'none' }}
      >
        <div
          ref={rootRef}
          // @ts-expect-error - xmlns is valid for XHTML in SVG foreignObject
          xmlns="http://www.w3.org/1999/xhtml"
          style={{ ...rootStyle, pointerEvents: 'auto' }}
          role="navigation"
          aria-label={structure.tree.label}
        >
          {treeContent}
        </div>
      </foreignObject>
    );
  }
);
